import React from 'react';
import type { SimpleBarOptions } from 'simplebar-react';
import { useVirtualizer } from '@tanstack/react-virtual';

import { useModulesScrollbarContext } from 'app/design-system/components/Scrollbar/ModulesScrollbarContext';
import type { SportId } from 'app/lib/betting/constants';
import { MatchesContainer } from 'app/modules/Betting';
import { MatchRows } from 'app/modules/Betting/components/MatchTable/components/MatchRows';
import { SubheaderBreadcrumbs } from 'app/modules/Betting/components/MatchTable/components/SubheaderBreadcrumbs';
import type { TournamentWithHierarchy } from 'app/redux/betting-presentation/grid/selectors/allSportsGrid';

// Описания типа
// range: {
//   startIndex: number;
//   endIndex: number;
//   overscan: number;
//   count: number;
// }
// взято из node_modules/@tanstack/virtual-core/src/index.ts 22:18
// Если делать import { Range } from '@tanstack/virtual-core/src'; то получаем ошибку типов внутри node_modules
// Попробовать import после обновления TS до версии 5+.

type VirtualizerProps = {
  sportId: SportId;
  tournamentIds: Array<number>;
  groupedTournamentsBySportId: Map<number, TournamentWithHierarchy>;
};

export const Virtualizer = ({
  sportId,
  tournamentIds,
  groupedTournamentsBySportId,
}: VirtualizerProps) => {
  const scrollbarContext = useModulesScrollbarContext();
  const scrollRef: React.RefObject<SimpleBarOptions> | undefined = scrollbarContext?.scrollbarRef;
  const scrollRefCurrent = scrollRef?.current;

  const rowVirtualizer = useVirtualizer({
    count: groupedTournamentsBySportId.size,
    getScrollElement: () => scrollRefCurrent?.contentWrapperEl,
    estimateSize: () => 0,
  });

  rowVirtualizer.shouldAdjustScrollPositionOnItemSizeChange = () => false;

  const items = rowVirtualizer.getVirtualItems();

  const result = items.reduce((acc, virtualItem) => {
    const tournamentId = tournamentIds[virtualItem.index];
    const matchIds = groupedTournamentsBySportId.get(tournamentId)?.matchIds;

    if (!matchIds || matchIds.length === 0) {
      return acc;
    }

    acc.push(
      <li
        ref={rowVirtualizer.measureElement}
        key={virtualItem.key}
        data-index={virtualItem.index}
        style={{
          position: 'absolute',
          width: '100%',
          top: `${virtualItem.start}px`,
        }}>
        <SubheaderBreadcrumbs matchIds={matchIds}
                              sportId={sportId} />
        <MatchRows matchIds={matchIds} />
      </li>,
    );

    return acc;
  }, [] as Array<JSX.Element>);

  return (
    <MatchesContainer
      style={{
        height: `${rowVirtualizer.getTotalSize()}px`,
        position: 'relative',
      }}>
      {result}
    </MatchesContainer>
  );
};
